import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { mockArticles } from '../../../mocks/blog';
import type { Article } from '../../../mocks/blog';
import BlogNav from '../components/BlogNav';
import ArticleContent from '../components/ArticleContent';
import SeoJsonLd from '@/components/feature/SeoJsonLd';
import PageSeo from '@/components/feature/PageSeo';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

function getArticleById(id: string): Article | null {
  try {
    const stored = localStorage.getItem('blog_articles');
    if (stored) {
      const parsed: Article[] = JSON.parse(stored);
      const found = parsed.find((a) => a.id === id && a.status === 'published');
      if (found) return found;
    }
  } catch {
    // ignore
  }
  return mockArticles.find((a) => a.id === id) ?? null;
}

function getRelated(current: Article, all: Article[]): Article[] {
  return all
    .filter((a) => a.id !== current.id && a.tags.some((t) => current.tags.includes(t)))
    .slice(0, 3);
}

function getAllArticles(): Article[] {
  try {
    const stored = localStorage.getItem('blog_articles');
    if (stored) {
      const parsed: Article[] = JSON.parse(stored);
      const published = parsed.filter((a) => a.status === 'published');
      return published.length > 0 ? published : mockArticles;
    }
  } catch {
    // ignore
  }
  return mockArticles;
}

function formatDate(dateStr: string) {
  const d = new Date(dateStr);
  return `${d.getFullYear()} 年 ${d.getMonth() + 1} 月 ${d.getDate()} 日`;
}

export default function BlogDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [article, setArticle] = useState<Article | null>(null);
  const [related, setRelated] = useState<Article[]>([]);
  const [visible, setVisible] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    if (!id) return;
    const found = getArticleById(id);
    setArticle(found);
    if (found) {
      const all = getAllArticles();
      setRelated(getRelated(found, all));
    }
    window.scrollTo({ top: 0 });
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, [id]);

  useEffect(() => {
    const handleScroll = () => {
      const el = document.documentElement;
      const scrollTop = el.scrollTop || document.body.scrollTop;
      const scrollHeight = el.scrollHeight - el.clientHeight;
      setScrollProgress(scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const articleJsonLd = article
    ? {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: article.title,
        description: article.summary,
        image: article.coverImage,
        datePublished: article.date,
        dateModified: article.date,
        url: `${SITE_URL}/blog/${article.id}`,
        inLanguage: 'zh-TW',
        author: {
          '@type': 'Person',
          name: 'Adam Chen',
        },
        publisher: {
          '@type': 'Organization',
          name: '四相AI影視學院',
          url: SITE_URL,
        },
        keywords: article.tags.join(','),
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `${SITE_URL}/blog/${article.id}`,
        },
      }
    : null;

  if (!article) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <BlogNav />
        <div className="text-center">
          <i className="ri-file-unknow-line text-5xl text-amber-500/20 mb-4 block" />
          <p className="text-white/40 text-sm tracking-widest" style={{ fontFamily: "'Noto Sans TC', sans-serif" }}>
            找不到這篇文章
          </p>
          <button
            onClick={() => navigate('/blog')}
            className="mt-6 px-6 py-2.5 border border-amber-500/30 text-amber-400 text-sm tracking-wider hover:bg-amber-500/10 transition-all duration-300 rounded-sm cursor-pointer whitespace-nowrap"
            style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
          >
            返回文章列表
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <PageSeo
        title={`${article.title} - 四相AI影視學院`}
        description={article.summary}
        keywords={article.tags.join(',')}
        canonical={`${SITE_URL}/blog/${article.id}`}
        ogType="article"
        ogImage={article.coverImage}
      />
      {articleJsonLd && <SeoJsonLd data={articleJsonLd} id="blog-detail-jsonld" />}

      {/* Reading progress bar */}
      <div
        className="fixed top-0 left-0 z-[60] h-0.5 bg-gradient-to-r from-amber-500 to-yellow-300 transition-all duration-100"
        style={{ width: `${scrollProgress}%` }}
      />

      <BlogNav />

      {/* Hero cover */}
      <section className="relative w-full h-[60vh] min-h-[420px] overflow-hidden">
        <img
          src={article.coverImage}
          alt={article.title}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/30" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 to-transparent" />

        {/* Back button */}
        <button
          onClick={() => navigate('/blog')}
          className={`absolute top-24 left-8 flex items-center gap-2 text-white/50 hover:text-amber-400 transition-all duration-300 cursor-pointer whitespace-nowrap group ${visible ? 'opacity-100' : 'opacity-0'}`}
          style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
        >
          <i className="ri-arrow-left-line text-sm transition-transform duration-300 group-hover:-translate-x-1" />
          <span className="text-xs tracking-[0.3em]">返回列表</span>
        </button>

        {/* Title area */}
        <div
          className={`absolute bottom-0 left-0 right-0 px-8 pb-12 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          <div className="max-w-4xl mx-auto">
            {/* Tags */}
            <div className="flex flex-wrap gap-2 mb-5">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-amber-500/20 border border-amber-500/40 text-amber-400 text-xs tracking-wider rounded-sm backdrop-blur-sm"
                  style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
                >
                  {tag}
                </span>
              ))}
            </div>

            <h1
              className="text-4xl md:text-5xl font-black leading-tight mb-5"
              style={{
                fontFamily: "'Noto Serif TC', serif",
                background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 40%, #fde68a 70%, #d97706 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                filter: 'drop-shadow(0 0 20px rgba(251,191,36,0.25))',
              }}
            >
              {article.title}
            </h1>

            <div className="flex items-center gap-6 text-white/40 text-xs tracking-[0.25em]" style={{ fontFamily: "'Noto Sans TC', sans-serif" }}>
              <div className="flex items-center gap-2">
                <i className="ri-calendar-line text-amber-500/50" />
                <span>{formatDate(article.date)}</span>
              </div>
              <div className="w-px h-3 bg-white/20" />
              <div className="flex items-center gap-2">
                <i className="ri-user-line text-amber-500/50" />
                <span>Adam Chen</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Article body */}
      <section
        className={`px-8 py-16 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
      >
        <div className="max-w-4xl mx-auto flex gap-12">
          {/* Main content */}
          <article className="flex-1 min-w-0">
            {/* Summary */}
            <div className="relative mb-10 pl-5 border-l-2 border-amber-500/50">
              <p
                className="text-white/60 text-base leading-relaxed italic"
                style={{ fontFamily: "'Noto Serif TC', serif" }}
              >
                {article.summary}
              </p>
            </div>

            {/* Divider */}
            <div className="flex items-center gap-4 mb-10">
              <div className="flex-1 h-px bg-gradient-to-r from-amber-500/30 to-transparent" />
              <i className="ri-quill-pen-line text-amber-500/40 text-sm" />
              <div className="flex-1 h-px bg-gradient-to-l from-amber-500/30 to-transparent" />
            </div>

            {/* Content */}
            <ArticleContent article={article} />

            {/* Bottom divider */}
            <div className="mt-16 pt-10 border-t border-amber-500/10">
              <div className="flex flex-wrap gap-2 mb-8">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-white/[0.04] border border-amber-500/15 text-amber-400/60 text-xs tracking-wider rounded-sm"
                    style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
                  >
                    # {tag}
                  </span>
                ))}
              </div>

              <button
                onClick={() => navigate('/blog')}
                className="flex items-center gap-3 text-white/40 hover:text-amber-400 transition-all duration-300 cursor-pointer group"
                style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
              >
                <i className="ri-arrow-left-line transition-transform duration-300 group-hover:-translate-x-1" />
                <span className="text-sm tracking-[0.2em]">返回文章列表</span>
              </button>
            </div>
          </article>

          {/* Sidebar */}
          <aside className="w-64 shrink-0 hidden lg:block">
            <div className="sticky top-28 space-y-8">
              {/* Author card */}
              <div className="bg-white/[0.03] border border-amber-500/10 rounded-sm p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-yellow-400 flex items-center justify-center shrink-0">
                    <span className="text-black font-bold text-sm">A</span>
                  </div>
                  <div>
                    <p className="text-white/80 text-sm font-semibold" style={{ fontFamily: "'Noto Serif TC', serif" }}>Adam Chen</p>
                    <p className="text-white/30 text-xs tracking-wider" style={{ fontFamily: "'Noto Sans TC', sans-serif" }}>AI 影視創作者</p>
                  </div>
                </div>
                <p className="text-white/35 text-xs leading-relaxed" style={{ fontFamily: "'Noto Sans TC', sans-serif" }}>
                  專注於 AI 虛擬人 IP、3D 動畫與品牌視覺創作，分享創作思考與技術洞察。
                </p>
              </div>

              {/* Related articles */}
              {related.length > 0 && (
                <div>
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-4 h-px bg-amber-500/50" />
                    <span className="text-amber-500/60 text-xs tracking-[0.3em]" style={{ fontFamily: "'Noto Sans TC', sans-serif" }}>相關文章</span>
                  </div>
                  <div className="space-y-4">
                    {related.map((r) => (
                      <div
                        key={r.id}
                        onClick={() => navigate(`/blog/${r.id}`)}
                        className="group cursor-pointer"
                      >
                        <div className="w-full h-28 overflow-hidden rounded-sm mb-3">
                          <img
                            src={r.coverImage}
                            alt={r.title}
                            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                          />
                        </div>
                        <p
                          className="text-white/60 text-xs leading-snug group-hover:text-amber-400 transition-colors duration-300 line-clamp-2"
                          style={{ fontFamily: "'Noto Serif TC', serif" }}
                        >
                          {r.title}
                        </p>
                        <p className="text-white/25 text-xs mt-1 tracking-wider" style={{ fontFamily: "'Noto Sans TC', sans-serif" }}>
                          {formatDate(r.date)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </aside>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative mt-10 px-8 py-12 border-t border-amber-500/10">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-8">
            <button
              onClick={() => navigate('/')}
              className="text-white/30 hover:text-amber-400 transition-colors duration-300 text-sm cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
            >
              回到首頁
            </button>
            <button
              onClick={() => navigate('/blog')}
              className="text-white/30 hover:text-amber-400 transition-colors duration-300 text-sm cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
            >
              文章列表
            </button>
          </div>
          <p className="text-white/20 text-xs tracking-widest" style={{ fontFamily: "'Noto Sans TC', sans-serif" }}>
            © 2025 Adam Chen. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
